/** Pure wall-clock resolution. No device timezone or fixed DST width assumptions. */
export type LocalTimeCandidate = { instantIso: string; offsetMinutes: number };
export type LocalTimeResolution =
  | { kind: 'invalid'; candidates: [] }
  | { kind: 'nonexistent'; candidates: []; earlier: string; later: string }
  | { kind: 'unique' | 'ambiguous'; candidates: LocalTimeCandidate[] };

export type LocalTimeOptions = {
  /** Actual evidence defaults to reject; recurrence must choose its policy. */
  disambiguation?: 'reject' | 'earlier' | 'later' | 'compatible';
  /** Retain the instant when editing an unchanged wall time. */
  preferredInstant?: string | null;
};

function wallMs(instant: number, formatter: Intl.DateTimeFormat): number {
  const parts = formatter.formatToParts(new Date(instant));
  const part = (name: string) => Number(parts.find((p) => p.type === name)?.value);
  const date = new Date(0);
  date.setUTCFullYear(part('year'), part('month') - 1, part('day'));
  date.setUTCHours(part('hour'), part('minute'), part('second'), 0);
  return date.getTime() + (((instant % 1000) + 1000) % 1000);
}

/** Enumerate actual offset candidates around a wall date, including half-hour folds. */
export function resolveLocalDateTime(
  input: string | null | undefined,
  timezone: string,
): LocalTimeResolution {
  const invalid: LocalTimeResolution = { kind: 'invalid', candidates: [] };
  const match = input?.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(?:([+-])(\d{2}):(\d{2}))?$/,
  );
  if (!match) return invalid;
  const [, y, m, d, h, minute, second = '0', millis = '0', sign, offsetHour, offsetMinute] = match;
  const wall = new Date(0);
  wall.setUTCFullYear(Number(y), Number(m) - 1, Number(d));
  wall.setUTCHours(Number(h), Number(minute), Number(second), Number(millis.padEnd(3, '0')));
  if (
    Number(y) < 1 ||
    wall.getUTCFullYear() !== Number(y) ||
    wall.getUTCMonth() + 1 !== Number(m) ||
    wall.getUTCDate() !== Number(d) ||
    Number(h) > 23 ||
    Number(minute) > 59 ||
    Number(second) > 59 ||
    (sign && (Number(offsetHour) > 23 || Number(offsetMinute) > 59))
  )
    return invalid;
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    const target = wall.getTime();
    const offsets = new Set<number>();
    // IANA transitions surrounding the target date. Offset width is measured,
    // never assumed to be an hour; the wider window also covers date-line jumps.
    for (let hours = -48; hours <= 48; hours += 12) {
      const sample = target + hours * 3_600_000;
      offsets.add(wallMs(sample, formatter) - sample);
    }
    const explicitOffset = sign
      ? (sign === '-' ? -1 : 1) * (Number(offsetHour) * 60 + Number(offsetMinute))
      : null;
    const candidates = [...offsets]
      .map((offset) => ({
        instantIso: new Date(target - offset).toISOString(),
        offsetMinutes: offset / 60_000,
      }))
      .filter(
        (candidate) =>
          wallMs(Date.parse(candidate.instantIso), formatter) === target &&
          (explicitOffset === null || candidate.offsetMinutes === explicitOffset),
      )
      .sort((a, b) => a.instantIso.localeCompare(b.instantIso));
    if (candidates.length)
      return { kind: candidates.length === 1 ? 'unique' : 'ambiguous', candidates };
    // An explicit offset that is not valid in this zone must never snap.
    if (explicitOffset !== null) return invalid;
    const projections = [...offsets].map((offset) => {
      const instant = target - offset;
      return { instant, delta: wallMs(instant, formatter) - target };
    });
    const earlier = projections.filter((p) => p.delta < 0).sort((a, b) => b.delta - a.delta)[0];
    const later = projections.filter((p) => p.delta > 0).sort((a, b) => a.delta - b.delta)[0];
    if (!earlier || !later) return invalid;
    return {
      kind: 'nonexistent',
      candidates: [],
      earlier: new Date(earlier.instant).toISOString(),
      later: new Date(later.instant).toISOString(),
    };
  } catch {
    return invalid;
  }
}

/** Resolve a capture only when unique, explicitly disambiguated or unchanged. */
export function localDateTimeToUtcIso(
  input: string | null | undefined,
  timezone: string,
  options: LocalTimeOptions = {},
): string | null {
  const resolved = resolveLocalDateTime(input, timezone);
  if (resolved.kind === 'invalid') return null;
  if (resolved.kind === 'nonexistent') {
    if (options.disambiguation === 'earlier') return resolved.earlier;
    if (options.disambiguation === 'later' || options.disambiguation === 'compatible')
      return resolved.later;
    return null;
  }
  const preferred = options.preferredInstant && Date.parse(options.preferredInstant);
  const unchanged = resolved.candidates.find((c) => Date.parse(c.instantIso) === preferred);
  if (unchanged) return unchanged.instantIso;
  if (resolved.kind === 'unique') return resolved.candidates[0]!.instantIso;
  if (options.disambiguation === 'earlier' || options.disambiguation === 'compatible')
    return resolved.candidates[0]!.instantIso;
  if (options.disambiguation === 'later')
    return resolved.candidates[resolved.candidates.length - 1]!.instantIso;
  return null;
}
